import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import Swal from 'sweetalert2';
import ModalCobro from '../components/ModalCobro';
import Ticket from '../components/Ticket';

export default function OrdenMesa() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const [vistaActiva, setVistaActiva] = useState('productos');
  const [orden, setOrden] = useState(null);
  const [productos, setProductos] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [agregando, setAgregando] = useState({});
  const [categoriaActiva, setCategoriaActiva] = useState('todas');
  const [showTipoCuentaModal, setShowTipoCuentaModal] = useState(false);
  const [showSeparadaModal, setShowSeparadaModal] = useState(false);
  const [itemsSeleccionados, setItemsSeleccionados] = useState([]);
  const [itemsYaFacturados, setItemsYaFacturados] = useState(
    location.state?.items_ya_facturados || []
  );
  const [showModalCobro, setShowModalCobro] = useState(false);
  const [itemsParaCobro, setItemsParaCobro] = useState([]);
  const [totalCobro, setTotalCobro] = useState(0);
  const [saleResult, setSaleResult] = useState(null);
  const [esSeparada, setEsSeparada] = useState(false);

  useEffect(() => {
    cargarDatos();
    const interval = setInterval(cargarOrden, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (location.state?.abrir_separada && !loading) {
      setItemsSeleccionados([]);
      setShowSeparadaModal(true);
    }
  }, [loading]);

  const cargarOrden = async () => {
    try {
      const res = await api.get(`/ordenes/${id}`);
      setOrden(res.data);
    } catch (error) {
      console.error('Error recargando orden:', error);
    }
  };

  const cargarDatos = async () => {
    try {
      const [ordenRes, productosRes] = await Promise.all([
        api.get(`/ordenes/${id}`),
        api.get('/restaurante/productos'),
      ]);
      setOrden(ordenRes.data);
      setProductos(productosRes.data);
    } catch (error) {
      Swal.fire('Error', 'No se pudo cargar la orden', 'error');
    } finally {
      setLoading(false);
    }
  };

  const agregarProducto = async (producto) => {
    if (agregando[producto.id]) return;
    setAgregando(prev => ({ ...prev, [producto.id]: true }));

    const itemExistente = orden?.items?.find(
      item => item.product_id === producto.id && !item.enviado_cocina
    );

    if (itemExistente) {
      setOrden(prev => ({
        ...prev,
        items: prev.items.map(item =>
          item.id === itemExistente.id
            ? { ...item, cantidad: item.cantidad + 1, subtotal: (item.cantidad + 1) * parseFloat(item.precio_unitario) }
            : item
        ),
        total: (parseFloat(prev.total) + parseFloat(producto.precio_venta)).toFixed(2),
        subtotal: (parseFloat(prev.subtotal) + parseFloat(producto.precio_venta)).toFixed(2),
      }));
    } else {
      const itemTemporal = {
        id: `temp-${Date.now()}`,
        product_id: producto.id,
        producto: producto,
        cantidad: 1,
        precio_unitario: producto.precio_venta,
        subtotal: producto.precio_venta,
        enviado_cocina: false,
        listo: false,
      };
      setOrden(prev => ({
        ...prev,
        items: [...(prev.items || []), itemTemporal],
        total: (parseFloat(prev.total) + parseFloat(producto.precio_venta)).toFixed(2),
        subtotal: (parseFloat(prev.subtotal) + parseFloat(producto.precio_venta)).toFixed(2),
      }));
    }

    try {
      if (itemExistente) {
        await api.put(`/ordenes/items/${itemExistente.id}`, {
          cantidad: itemExistente.cantidad + 1
        });
      } else {
        await api.post(`/ordenes/${id}/items`, {
          items: [{ product_id: producto.id, cantidad: 1 }]
        });
      }
      cargarOrden();
    } catch (error) {
      cargarOrden();
      Swal.fire('Error', 'No se pudo agregar el producto', 'error');
    } finally {
      setAgregando(prev => ({ ...prev, [producto.id]: false }));
    }
  };

  const eliminarItem = async (itemId) => {
    const itemAEliminar = orden?.items?.find(i => i.id === itemId);
    if (!itemAEliminar) return;

    setOrden(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId),
      total: Math.max(0, parseFloat(prev.total) - parseFloat(itemAEliminar.subtotal)).toFixed(2),
      subtotal: Math.max(0, parseFloat(prev.subtotal) - parseFloat(itemAEliminar.subtotal)).toFixed(2),
    }));

    try {
      await api.delete(`/ordenes/items/${itemId}`);
      cargarOrden();
    } catch (error) {
      cargarOrden();
      Swal.fire('Error', error.response?.data?.message || 'No se pudo eliminar el producto', 'error');
    }
  };

  const enviarCocina = async () => {
    try {
      await api.post(`/ordenes/${id}/enviar-cocina`);

      const ordenActualizada = await api.get(`/ordenes/${id}`);
      const itemsEnviados = ordenActualizada.data.items.filter(item => item.enviado_cocina);

      await api.post('/print-jobs', {
        tipo: 'comanda',
        referencia_id: parseInt(id),
        datos: { item_ids: itemsEnviados.map(i => i.id) },
      });

      setOrden(ordenActualizada.data);

      Swal.fire({
        icon: 'success',
        title: '✅ Comanda enviada',
        text: 'La comanda se imprimirá en cocina',
        timer: 1500,
        showConfirmButton: false,
      });

    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'No se pudo imprimir la comanda', 'error');
    }
  };

  const irAFacturarUna = () => {
    const items = orden.items.map(item => ({
      uniqueKey: `${item.product_id}-${item.id}`,
      product_id: item.product_id,
      presentation_id: null,
      codigo: item.producto?.codigo || `PROD-${item.product_id}`,
      nombre: item.producto?.nombre,
      precio_unitario: parseFloat(item.precio_unitario),
      cantidad: item.cantidad,
      subtotal: parseFloat(item.subtotal),
      gravado: Boolean(item.producto?.aplica_iva),
    }));

    const total = items.reduce((acc, i) => acc + i.subtotal, 0);

    setItemsParaCobro(items);
    setTotalCobro(total);
    setEsSeparada(false);
    setShowTipoCuentaModal(false);
    setShowModalCobro(true);
  };

  const abrirSeparada = () => {
    setShowTipoCuentaModal(false);
    setItemsSeleccionados([]);
    setShowSeparadaModal(true);
  };

  const toggleItem = (itemId) => {
    setItemsSeleccionados(prev =>
      prev.includes(itemId) ? prev.filter(i => i !== itemId) : [...prev, itemId]
    );
  };

  const itemsPendientesSeparada = orden?.items?.filter(
    item => !itemsYaFacturados.includes(item.id)
  ) || [];

  const irAFacturarSeparada = () => {
    if (itemsSeleccionados.length === 0) {
      Swal.fire('Error', 'Selecciona al menos un producto', 'warning');
      return;
    }

    const items = orden.items
      .filter(item => itemsSeleccionados.includes(item.id))
      .map(item => ({
        uniqueKey: `${item.product_id}-${item.id}`,
        product_id: item.product_id,
        presentation_id: null,
        codigo: item.producto?.codigo || `PROD-${item.product_id}`,
        nombre: item.producto?.nombre,
        precio_unitario: parseFloat(item.precio_unitario),
        cantidad: item.cantidad,
        subtotal: parseFloat(item.subtotal),
        gravado: Boolean(item.producto?.aplica_iva),
      }));

    const total = items.reduce((acc, i) => acc + i.subtotal, 0);

    setItemsParaCobro(items);
    setTotalCobro(total);
    setEsSeparada(true);
    setShowSeparadaModal(false);
    setShowModalCobro(true);
  };

  const hayItemsSinEnviar = orden?.items?.some(item => !item.enviado_cocina);

  const productosFiltrados = productos.filter(p => {
    const coincideBusqueda = p.nombre.toLowerCase().includes(busqueda.toLowerCase());
    const coincideCategoria = categoriaActiva === 'todas' || p.categoria === categoriaActiva;
    return coincideBusqueda && coincideCategoria;
  });

  const getFotoUrl = (fotoPath) => {
    if (!fotoPath) return null;
    if (fotoPath.startsWith('http')) return fotoPath;
    return `https://restaurante.altivaweb.com/BACK/public/storage/${fotoPath}`;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <div className="p-3" style={{ height: '100vh' }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <button className="btn btn-outline-secondary btn-sm me-2" onClick={() => navigate(orden?.tipo === 'para_llevar' ? '/para-llevar' : '/mesas')}>
            ← Volver
          </button>
          {orden?.tipo === 'para_llevar' ? (
            <strong>
              📦 Para Llevar #{orden?.id}
              {orden?.nombre_cliente && <span className="text-muted ms-2">— {orden.nombre_cliente}</span>}
            </strong>
          ) : (
            <>
              <strong>Mesa {orden?.mesa?.numero}</strong>
              <span className="text-muted ms-2">— {orden?.mesero?.nombre}</span>
            </>
          )}
        </div>
        <strong>Total: ${parseFloat(orden?.total || 0).toFixed(2)}</strong>
      </div>

      {isMobile && (
        <div style={{ display: 'flex', marginBottom: '12px', borderBottom: '2px solid #e2e8f0' }}>
          <button
            onClick={() => setVistaActiva('productos')}
            style={{
              flex: 1, padding: '10px', border: 'none', fontWeight: 600,
              background: vistaActiva === 'productos' ? '#667eea' : 'white',
              color: vistaActiva === 'productos' ? 'white' : '#4a5568',
              borderRadius: '8px 0 0 0',
            }}
          >
            🍽️ Productos
          </button>
          <button
            onClick={() => setVistaActiva('orden')}
            style={{
              flex: 1, padding: '10px', border: 'none', fontWeight: 600,
              background: vistaActiva === 'orden' ? '#667eea' : 'white',
              color: vistaActiva === 'orden' ? 'white' : '#4a5568',
              borderRadius: '0 8px 0 0',
            }}
          >
            🧾 Orden {orden?.items?.length > 0 && (
              <span style={{
                background: '#e53e3e', color: 'white', borderRadius: '50%',
                padding: '1px 6px', fontSize: '11px', marginLeft: '4px'
              }}>
                {orden.items.length}
              </span>
            )}
          </button>
        </div>
      )}

      <div className={isMobile ? '' : 'row h-100'}>
        <div
          className={isMobile ? '' : 'col-7 border-end'}
          style={{ display: isMobile && vistaActiva !== 'productos' ? 'none' : 'block' }}
        >
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
            {[
              { value: 'todas',  label: '🗂️ Todas' },
              { value: 'comida', label: '🍽️ Comida' },
              { value: 'bebida', label: '🥤 Bebidas' },
              { value: 'postre', label: '🍰 Postres' },
              { value: 'otro',   label: '📦 Otro' },
            ].map((cat) => (
              <button
                key={cat.value}
                onClick={() => setCategoriaActiva(cat.value)}
                style={{
                  padding: '5px 12px',
                  borderRadius: '16px',
                  border: '2px solid',
                  borderColor: categoriaActiva === cat.value ? '#667eea' : '#e2e8f0',
                  background: categoriaActiva === cat.value ? '#667eea' : 'white',
                  color: categoriaActiva === cat.value ? 'white' : '#4a5568',
                  fontWeight: 600,
                  fontSize: '12px',
                  cursor: 'pointer',
                }}
              >
                {cat.label}
              </button>
            ))}
          </div>

          <input
            type="text"
            className="form-control mb-3"
            placeholder="Buscar producto..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />

          <div className="row g-2" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
            {productosFiltrados.map((producto) => (
              <div key={producto.id} className={isMobile ? 'col-6' : 'col-4'}>
                <div
                  className="card text-center"
                  style={{
                    cursor: agregando[producto.id] ? 'not-allowed' : 'pointer',
                    opacity: agregando[producto.id] ? 0.7 : 1,
                    transition: 'opacity 0.2s',
                    userSelect: 'none',
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: '10px',
                  }}
                  onClick={() => agregarProducto(producto)}
                >
                  {producto.foto ? (
                    <img
                      src={getFotoUrl(producto.foto)}
                      alt={producto.nombre}
                      style={{ width: '100%', height: '90px', objectFit: 'cover' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div style={{
                      width: '100%', height: '90px', background: '#f7fafc',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px',
                    }}>
                      🍽️
                    </div>
                  )}
                  {agregando[producto.id] && (
                    <div style={{ position: 'absolute', top: 4, right: 6 }}>
                      <span className="spinner-border spinner-border-sm text-primary" style={{ width: 12, height: 12 }} />
                    </div>
                  )}
                  <div className="p-2">
                    <small className="fw-bold d-block" style={{ fontSize: '12px', lineHeight: '1.2' }}>
                      {producto.nombre}
                    </small>
                    {producto.descripcion && (
                      <small className="text-muted d-block" style={{ fontSize: '10px', marginTop: '2px', lineHeight: '1.2' }}>
                        {producto.descripcion}
                      </small>
                    )}
                    <small className="text-success fw-bold d-block" style={{ marginTop: '4px' }}>
                      ${parseFloat(producto.precio_venta).toFixed(2)}
                    </small>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          className={isMobile ? '' : 'col-5'}
          style={{ display: isMobile && vistaActiva !== 'orden' ? 'none' : 'block' }}
        >
          <h6 className="mb-3">Orden actual</h6>
          {!orden?.items || orden.items.length === 0 ? (
            <p className="text-muted text-center mt-4">No hay productos agregados</p>
          ) : (
            <>
              <div style={{ maxHeight: '55vh', overflowY: 'auto' }}>
                {orden.items.map((item) => (
                  <div key={item.id} className="d-flex justify-content-between align-items-center border-bottom py-2">
                    <div>
                      <small className="fw-bold">{item.producto?.nombre}</small>
                      {item.listo ? (
                        <span className="badge bg-success ms-1" style={{ fontSize: '10px' }}>✓ Listo</span>
                      ) : item.enviado_cocina ? (
                        <span className="badge bg-warning ms-1" style={{ fontSize: '10px' }}>Cocina</span>
                      ) : null}
                      <br />
                      <small className="text-muted">x{item.cantidad} — ${parseFloat(item.precio_unitario).toFixed(2)}</small>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <small className="fw-bold">${parseFloat(item.subtotal).toFixed(2)}</small>
                      {!item.enviado_cocina && (
                        <button
                          className="btn btn-sm btn-outline-danger"
                          style={{ padding: '2px 6px', fontSize: '12px' }}
                          onClick={() => eliminarItem(item.id)}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-top pt-2 mt-2">
                <div className="d-flex justify-content-between mb-2">
                  <strong>Total:</strong>
                  <strong>${parseFloat(orden?.total || 0).toFixed(2)}</strong>
                </div>
                {hayItemsSinEnviar && (
                  <button className="btn btn-warning w-100 mt-2" onClick={enviarCocina}>
                    🖨️ Imprimir Comanda
                  </button>
                )}
                <button
                  className="btn btn-success w-100 mt-2"
                  onClick={() => setShowTipoCuentaModal(true)}
                >
                  Cerrar Cuenta
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {isMobile && vistaActiva === 'productos' && orden?.items?.length > 0 && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          background: 'white', borderTop: '2px solid #e2e8f0',
          padding: '10px 16px', zIndex: 1000,
          boxShadow: '0 -4px 12px rgba(0,0,0,0.1)',
        }}>
          <div style={{ maxHeight: '150px', overflowY: 'auto', marginBottom: '8px' }}>
            {orden.items.map((item) => (
              <div key={item.id} style={{
                display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', padding: '4px 0',
                borderBottom: '1px solid #f0f0f0', fontSize: '13px',
              }}>
                <span><strong>x{item.cantidad}</strong> {item.producto?.nombre}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: '#38a169', fontWeight: 600 }}>
                    ${parseFloat(item.subtotal).toFixed(2)}
                  </span>
                  {!item.enviado_cocina && (
                    <button
                      onClick={() => eliminarItem(item.id)}
                      style={{ background: 'none', border: 'none', color: '#e53e3e', fontSize: '14px', cursor: 'pointer' }}
                    >✕</button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <strong>Total: ${parseFloat(orden?.total || 0).toFixed(2)}</strong>
            <div style={{ display: 'flex', gap: '8px' }}>
              {hayItemsSinEnviar && (
                <button className="btn btn-warning btn-sm" onClick={enviarCocina}>
                  🖨️ Imprimir
                </button>
              )}
              <button className="btn btn-success btn-sm" onClick={() => setShowTipoCuentaModal(true)}>
                Cerrar Cuenta
              </button>
            </div>
          </div>
        </div>
      )}

      {showTipoCuentaModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-sm">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Cerrar Cuenta</h5>
                <button className="btn-close" onClick={() => setShowTipoCuentaModal(false)} />
              </div>
              <div className="modal-body">
                <p className="text-muted mb-3">¿Cómo se va a pagar?</p>
                <div className="d-flex flex-column gap-3">
                  <button
                    className="btn btn-success btn-lg w-100"
                    onClick={irAFacturarUna}
                    disabled={isSubmitting}
                  >
                    🧾 Una sola cuenta
                  </button>
                  <button
                    className="btn btn-outline-primary btn-lg w-100"
                    onClick={abrirSeparada}
                  >
                    👥 Cuentas separadas
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showSeparadaModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title">Cuentas Separadas</h5>
                  <small className="text-muted">
                    Selecciona los items de esta persona
                    {itemsYaFacturados.length > 0 && (
                      <span className="badge bg-success ms-2">
                        {itemsYaFacturados.length} ya facturados
                      </span>
                    )}
                  </small>
                </div>
                <button className="btn-close" onClick={() => setShowSeparadaModal(false)} />
              </div>
              <div className="modal-body">
                {itemsPendientesSeparada.length === 0 ? (
                  <div className="text-center text-success py-3">
                    <div style={{ fontSize: 40 }}>✅</div>
                    <p className="mt-2 fw-bold">Todos los items han sido facturados</p>
                  </div>
                ) : (
                  <>
                    <p className="text-muted mb-3" style={{ fontSize: 13 }}>
                      Marca los productos que va a pagar esta persona:
                    </p>
                    {itemsPendientesSeparada.map((item) => (
                      <div
                        key={item.id}
                        className="d-flex align-items-center gap-3 border rounded p-2 mb-2"
                        style={{
                          cursor: 'pointer',
                          background: itemsSeleccionados.includes(item.id) ? '#f0fdf4' : 'white',
                          borderColor: itemsSeleccionados.includes(item.id) ? '#22c55e' : '#dee2e6',
                        }}
                        onClick={() => toggleItem(item.id)}
                      >
                        <input
                          type="checkbox"
                          checked={itemsSeleccionados.includes(item.id)}
                          onChange={() => toggleItem(item.id)}
                          style={{ width: 18, height: 18 }}
                        />
                        <div className="flex-grow-1">
                          <div className="fw-bold" style={{ fontSize: 14 }}>{item.producto?.nombre}</div>
                          <small className="text-muted">x{item.cantidad}</small>
                        </div>
                        <div className="fw-bold text-success">
                          ${parseFloat(item.subtotal).toFixed(2)}
                        </div>
                      </div>
                    ))}
                    {itemsSeleccionados.length > 0 && (
                      <div className="border-top pt-2 mt-2 d-flex justify-content-between fw-bold">
                        <span>Total seleccionado:</span>
                        <span className="text-success">
                          ${orden.items
                            .filter(i => itemsSeleccionados.includes(i.id))
                            .reduce((acc, i) => acc + parseFloat(i.subtotal), 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
              {itemsPendientesSeparada.length > 0 && (
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowSeparadaModal(false)}>
                    Cancelar
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={irAFacturarSeparada}
                    disabled={itemsSeleccionados.length === 0}
                  >
                    Ir a Cobrar →
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showModalCobro && (
        <ModalCobro
          items={itemsParaCobro}
          total={totalCobro}
          ordenId={id}
          esSeparada={esSeparada}
          onSuccess={(data) => {
            setSaleResult(data);
            setShowModalCobro(false);
          }}
          onCancel={() => setShowModalCobro(false)}
        />
      )}

      {saleResult && (
        <Ticket
          invoice={saleResult}
          onAfterPrint={() => {
            setSaleResult(null);
            if (!esSeparada) {
              navigate('/mesas');
            } else {
              const todosFacturados = itemsYaFacturados.length + itemsSeleccionados.length >= orden?.items?.length;
              if (todosFacturados) {
                navigate('/mesas');
              } else {
                setItemsYaFacturados(prev => [...prev, ...itemsSeleccionados]);
                setItemsSeleccionados([]);
              }
            }
          }}
        />
      )}
    </div>
  );
}